import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import { PiUserListLight } from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { fetchMatterDropdowns } from '../../store/actions/Matters/MatterActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import SkeletonDropdownItem from '../shared/TailwindComponents/SkeletonDropdownItem';
import Badge from '../shared/TailwindComponents/Badge';

export interface MatterClient {
  client_id: number;
  client_uuid: string;
  name: string;
  email: string;
  role_name: string;
  role_id: string;
  contact_phone?: string;
  added_at?: string;
}

interface MatterClientsProps {
  matterUuid: string;
  clients?: MatterClient[];
  canEdit?: boolean;
  onUpdate?: () => void;
  isLoading?: boolean;
}

const MatterClients: React.FC<MatterClientsProps> = ({
  matterUuid,
  clients = [],
  canEdit = false,
  onUpdate,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [localClients, setLocalClients] = useState<MatterClient[]>(clients);
  const [clientRoles, setClientRoles] = useState<DropdownOption[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  useEffect(() => {
    setLocalClients(clients);
  }, [clients]);

  // Load client roles on mount
  useEffect(() => {
    const loadClientRoles = async () => {
      try {
        const result = await dispatch(fetchMatterDropdowns());
        if (result && result.clientRoles) {
          setClientRoles(result.clientRoles);
        }
      } catch (error) {
        console.error('Failed to load client roles:', error);
      }
    };

    loadClientRoles();
  }, [dispatch]);

  const handleOpenAddClientModal = useCallback(async () => {
    try {
      setIsLoadingClients(true);
      dispatch(showSidePanel({
        type: 'addClientToMatter',
        showSidePanel: true,
        width: '50%',
        message: t('Manage Contacts'),
        icon: <PiUserListLight className="w-5 h-5" />,
        subMsg: t('Add or remove contacts linked to this matter.'),
        matterUuid,
        clientRoleOptions: clientRoles,
        existingMatterClients: localClients,
        onCancel: () => dispatch(hideSidePanel()),
        onRefresh: async () => {
          if (onUpdate) onUpdate();
        },
        onAfterSave: async () => {
          if (onUpdate) onUpdate();
        }
      } as any));
    } catch (error) {
      console.error('Error opening manage contacts panel:', error);
    } finally {
      setIsLoadingClients(false);
    }
  }, [dispatch, matterUuid, clientRoles, localClients, t, onUpdate]);

  const handleNavigateToClient = (clientUuid: string) => {
    history.push(`/contacts/${clientUuid}`);
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <PiUserListLight className="h-5 w-5 text-primary-600" />
          <div className="text-sm font-light">{t('Clients')}</div>
        </div>
        <div className="space-y-2">
          <SkeletonDropdownItem count={2} />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      {/* Header */}
      <div className="mb-3 flex items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <PiUserListLight className="h-5 w-5 text-primary-600" />
          <div className="text-sm font-light">
            {t('Clients')} ({localClients.length})
          </div>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            size="small"
            onClick={handleOpenAddClientModal}
            disabled={isLoadingClients}
          >
            {isLoadingClients ? t('Loading...') : t('Manage')}
          </Button>
        )}
      </div>

      {localClients.length > 0 ? (
        <div className="space-y-2">
          {localClients.map((client, index) => (
            <div
              key={`${client.client_uuid || client.client_id || 'row'}-${index}`}
              className="flex items-center justify-between gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              {/* Client Info */}
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => handleNavigateToClient(client.client_uuid)}
                  className="block w-full text-sm font-medium text-gray-900 dark:text-gray-100 hover:underline hover:decoration-black dark:hover:decoration-white transition-all cursor-pointer text-left truncate"
                  title={client.name}
                >
                  {client.name}
                </button>
                {client.email && (
                  <div className="flex items-center gap-2 mt-1 min-w-0">
                    <a
                      href={`mailto:${client.email}`}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate min-w-0"
                    >
                      {client.email}
                    </a>
                  </div>
                )}
              </div>

              {/* Read-only role badge — full management lives in the side panel */}
              <div className="flex-shrink-0">
                <Badge color="blue">{client.role_name}</Badge>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('No clients assigned to this matter')}
          </p>
        </div>
      )}
    </div>
  );
};

export default MatterClients;
